export const PUBLIC_RESPONSE_SCHEMA_VERSION = 2;
export const PUBLIC_RESPONSE_SCHEMA_VERSION_V3 = 3;

export const PUBLIC_RESPONSE_INTENTS = Object.freeze([
  "profitability_guide",
  "property_value",
  "property_sale",
]);

export const LEGACY_OWNER_RESPONSE_INTENT = "legacy_owner_response";

const INTENT_SET = new Set(PUBLIC_RESPONSE_INTENTS);
const LANGUAGES = new Set(["fr", "en"]);
const UNIT_MIX_KEYS = Object.freeze([
  "oneAndHalf",
  "twoAndHalf",
  "threeAndHalf",
  "fourAndHalf",
  "fiveAndHalf",
  "sixAndHalfPlus",
  "commercial",
  "other",
]);
const EXPENSE_CENTS_KEYS = Object.freeze([
  "municipalTaxesCents",
  "schoolTaxesCents",
  "insuranceCents",
  "heatingElectricityCents",
  "otherExpensesCents",
]);

const trim = (value) => (typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "");

function assertIntent(intent) {
  if (!INTENT_SET.has(intent)) throw new TypeError("intent is invalid");
}

function assertExactKeys(value, allowed, label) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${label} is invalid`);
  }
  for (const key of Object.keys(value)) {
    if (!allowed.has(key)) throw new TypeError(`${label} contains unsupported field`);
  }
}

/**
 * Build the exact callable DTO from browser form values.
 * property_value emits schema V3; property_sale / guide emit schema V2.
 * Never includes campaign/property IDs from the browser.
 */
export function buildSubmitPublicCampaignMailingResponseRequest({ token, operationId, formResponse }) {
  if (!trim(token) || !trim(operationId)) throw new TypeError("Submission identifiers are required");
  const intent = formResponse?.intent;
  assertIntent(intent);
  if (!LANGUAGES.has(formResponse?.language)) throw new TypeError("language is invalid");
  if (formResponse?.consentAccepted !== true) throw new TypeError("consentAccepted is invalid");

  if (intent === "property_value" && formResponse?.schemaVersion === PUBLIC_RESPONSE_SCHEMA_VERSION_V3) {
    return {
      token: trim(token),
      operationId: trim(operationId),
      response: buildPropertyValueV3RequestBody(formResponse),
    };
  }

  if (formResponse?.schemaVersion !== PUBLIC_RESPONSE_SCHEMA_VERSION) {
    throw new TypeError("schemaVersion is invalid");
  }

  const fullName = trim(formResponse?.contact?.fullName);
  const email = trim(formResponse?.contact?.email).toLowerCase();
  const phoneRaw = formResponse?.contact?.phone;
  const phone = phoneRaw == null || phoneRaw === "" ? null : trim(phoneRaw);

  if (!fullName || !email) throw new TypeError("contact is invalid");

  const contact = { fullName, email, phone };
  const response = {
    schemaVersion: PUBLIC_RESPONSE_SCHEMA_VERSION,
    intent,
    contact,
    language: formResponse.language,
    consentAccepted: true,
  };

  if (intent === "property_sale") {
    if (!phone) throw new TypeError("phone is required");
    if (formResponse?.ownershipAttested !== true) throw new TypeError("ownershipAttested is required");
    response.ownershipAttested = true;
  } else if (formResponse?.ownershipAttested != null && formResponse.ownershipAttested !== false) {
    throw new TypeError("ownershipAttested is not allowed for this intent");
  }

  return { token: trim(token), operationId: trim(operationId), response };
}

function buildPropertyValueV3RequestBody(formResponse) {
  assertExactKeys(
    formResponse,
    new Set([
      "schemaVersion",
      "intent",
      "contact",
      "language",
      "consentAccepted",
      "financialSnapshot",
      "propertyProfile",
    ]),
    "response",
  );
  if (formResponse.intent !== "property_value") throw new TypeError("intent is invalid");
  assertExactKeys(formResponse.contact, new Set(["fullName", "email", "phone"]), "contact");
  assertExactKeys(
    formResponse.financialSnapshot,
    new Set(["currency", "period", "grossAnnualIncomeCents", "expenses", "otherExpensesDescription"]),
    "financialSnapshot",
  );
  assertExactKeys(formResponse.financialSnapshot.expenses, new Set(EXPENSE_CENTS_KEYS), "expenses");
  assertExactKeys(
    formResponse.propertyProfile,
    new Set(["constructionYear", "constructionYearUnknown", "unitMix", "unitCountMismatchConfirmed"]),
    "propertyProfile",
  );
  assertExactKeys(formResponse.propertyProfile.unitMix, new Set(UNIT_MIX_KEYS), "unitMix");

  const fullName = trim(formResponse.contact.fullName);
  const email = trim(formResponse.contact.email).toLowerCase();
  if (!fullName || !email) throw new TypeError("contact is invalid");
  if (formResponse.contact.phone != null) throw new TypeError("phone must be null for property_value");

  const constructionYearUnknown = formResponse.propertyProfile.constructionYearUnknown;
  const unitCountMismatchConfirmed = formResponse.propertyProfile.unitCountMismatchConfirmed;
  if (typeof constructionYearUnknown !== "boolean") {
    throw new TypeError("constructionYearUnknown is invalid");
  }
  if (typeof unitCountMismatchConfirmed !== "boolean") {
    throw new TypeError("unitCountMismatchConfirmed is invalid");
  }

  const otherExpensesDescription = formResponse.financialSnapshot.otherExpensesDescription;
  if (otherExpensesDescription !== null && typeof otherExpensesDescription !== "string") {
    throw new TypeError("otherExpensesDescription is invalid");
  }
  const otherCents = formResponse.financialSnapshot.expenses.otherExpensesCents;
  let normalizedDescription = null;
  if (otherCents > 0) {
    const trimmed = typeof otherExpensesDescription === "string" ? otherExpensesDescription.trim() : "";
    if (!trimmed) throw new TypeError("otherExpensesDescription is required");
    normalizedDescription = trimmed.replace(/\s+/g, " ");
  } else if (otherExpensesDescription == null) {
    normalizedDescription = null;
  } else if (typeof otherExpensesDescription === "string") {
    if (otherExpensesDescription.trim()) {
      throw new TypeError("otherExpensesDescription must be empty when otherExpensesCents is zero");
    }
    normalizedDescription = null;
  } else {
    throw new TypeError("otherExpensesDescription is invalid");
  }

  // Pass through already-parsed integer cents from the valuation builder; server re-validates.
  return {
    schemaVersion: PUBLIC_RESPONSE_SCHEMA_VERSION_V3,
    intent: "property_value",
    contact: { fullName, email, phone: null },
    language: formResponse.language,
    consentAccepted: true,
    financialSnapshot: {
      currency: "CAD",
      period: "annual_current",
      grossAnnualIncomeCents: formResponse.financialSnapshot.grossAnnualIncomeCents,
      expenses: { ...formResponse.financialSnapshot.expenses },
      otherExpensesDescription: normalizedDescription,
    },
    propertyProfile: {
      constructionYear: formResponse.propertyProfile.constructionYear,
      constructionYearUnknown,
      unitMix: { ...formResponse.propertyProfile.unitMix },
      unitCountMismatchConfirmed,
    },
  };
}

/** Reject rather than project unexpected callable responses. */
export function parsePublicCampaignMailingResponseReceipt(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("Invalid response receipt");
  }
  const keys = Object.keys(value);
  if (
    keys.some((key) => !["status", "submittedAt", "duplicate", "intent"].includes(key)) ||
    value.status !== "received" ||
    typeof value.duplicate !== "boolean" ||
    value.submittedAt == null ||
    !INTENT_SET.has(value.intent)
  ) {
    throw new TypeError("Invalid response receipt");
  }
  return {
    status: value.status,
    submittedAt: value.submittedAt,
    duplicate: value.duplicate,
    intent: value.intent,
  };
}

export function parsePublicCampaignMailingAccessDto(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("Invalid access DTO");
  }
  const property = value.property;
  if (!property || typeof property !== "object" || Array.isArray(property)) {
    throw new TypeError("Invalid access property");
  }
  const receivedIntents = Array.isArray(value.receivedIntents)
    ? value.receivedIntents.filter(
        (intent) => INTENT_SET.has(intent) || intent === LEGACY_OWNER_RESPONSE_INTENT,
      )
    : [];
  const dto = {
    property: {
      address: typeof property.address === "string" ? property.address : "",
      city: typeof property.city === "string" ? property.city : "",
      province: typeof property.province === "string" ? property.province : "",
      postalCode: typeof property.postalCode === "string" ? property.postalCode : "",
    },
    receivedIntents,
  };
  if (Number.isInteger(property.numberOfUnits) && property.numberOfUnits > 0) {
    dto.property.numberOfUnits = property.numberOfUnits;
  }
  // Strict optional year: integer within construction bounds only (no inference).
  const nowYear = new Date().getFullYear();
  if (
    Number.isInteger(property.constructionYear) &&
    property.constructionYear >= 1800 &&
    property.constructionYear <= nowYear
  ) {
    dto.property.constructionYear = property.constructionYear;
  }
  if (value.responseStatus === "received" || value.responseStatus === "not_submitted") {
    dto.responseStatus = value.responseStatus;
  }
  return dto;
}

export function createPublicResponseOperationId(cryptoApi = globalThis.crypto) {
  if (typeof cryptoApi?.randomUUID === "function") return cryptoApi.randomUUID();
  if (typeof cryptoApi?.getRandomValues !== "function") throw new Error("Secure randomness unavailable");
  const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
